import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { TrackableFile } from 'types/trackables';
import APIError from 'types/api_error';
import { OffsetPaginated } from 'types/pagination';

import { useAPI } from 'hooks/api/useAPI';

export interface UseTrackableFilesParams {
  trackable_list_id: string;
  trackable_item_id: string;
  trackable_file_ids?: string[];
  with_thumbnail?: boolean;
  pagination?: 'cursor' | 'offset';
  next_cursor?: string;
  prev_cursor?: string;
  page?: number;
  limit?: number;
}

export function useTrackableFiles({
  params,
  enabled = true,
  keepPreviousData = false,
  ...options
}: {
  params: UseTrackableFilesParams;
  enabled?: boolean;
  keepPreviousData?: boolean;
} & Omit<UseQueryOptions<OffsetPaginated<TrackableFile>, APIError>, 'queryKey' | 'queryFn'>) {
  const { applicationID, client } = useAPI();

  return useQuery<OffsetPaginated<TrackableFile>, APIError>(
    ['trackable-files', applicationID, params],
    async () => {
      const searchParams = new URLSearchParams();
      
      // Add filters
      if (params.trackable_file_ids?.length) {
        params.trackable_file_ids.forEach(id => searchParams.append('trackable_file_ids[]', id));
      }
      if (params.with_thumbnail !== undefined) {
        searchParams.append('with_thumbnail', params.with_thumbnail.toString());
      }

      // Add pagination (required by API)
      searchParams.append('pagination', params.pagination || 'offset');
      if (params.next_cursor) {
        searchParams.append('next_cursor', params.next_cursor);
      }
      if (params.prev_cursor) {
        searchParams.append('prev_cursor', params.prev_cursor);
      }
      if (params.page) {
        searchParams.append('page', params.page.toString());
      }
      if (params.limit) {
        searchParams.append('limit', params.limit.toString());
      }

      const query = searchParams.toString();
      const url = `/apps/${applicationID}/trackables/${params.trackable_list_id}/items/${params.trackable_item_id}/files${query ? `?${query}` : ''}`;
      
      const response = await client.get(url);
      return response.data;
    },
    {
      enabled: enabled && Boolean(applicationID && params.trackable_list_id && params.trackable_item_id),
      keepPreviousData,
      ...options,
    }
  );
}